import React from 'react'
import { Card, Text, Stack, Box, Heading, Flex, Badge, Grid } from '@sanity/ui'
import { Code } from '@sanity/ui/code'

const Section = ({ title, children }: { title: string; children: React.ReactNode }) => (
  <Card padding={4} radius={2} shadow={1} marginBottom={2}>
    <Stack gap={4}>
      <Heading size={1}>{title}</Heading>
      {children}
    </Stack>
  </Card>
)

const SubSection = ({ title, children }: { title: string; children: React.ReactNode }) => (
  <Box marginBottom={3}>
    <Text size={1} weight="semibold" style={{ marginBottom: '8px' }}>{title}</Text>
    {children}
  </Box>
)

const CodeBlock = ({ children }: { children: string }) => (
  <Box padding={3} style={{ backgroundColor: '#1a1a1a', borderRadius: '4px', overflow: 'auto' }}>
    <pre style={{ margin: 0, fontSize: '11px', color: '#e0e0e0', whiteSpace: 'pre-wrap' }}>
      {children}
    </pre>
  </Box>
)

const FeatureCard = ({ title, description, badge }: { title: string; description: string; badge?: string }) => (
  <Card padding={3} radius={2} tone="default" style={{ border: '1px solid #333' }}>
    <Stack gap={2}>
      <Flex align="center" gap={2}>
        <Text size={1} weight="semibold">{title}</Text>
        {badge && <Badge tone="primary" fontSize={0}>{badge}</Badge>}
      </Flex>
      <Text size={1} muted>{description}</Text>
    </Stack>
  </Card>
)

export default function AIRulesOverview() {
  return (
    <Stack gap={4} paddingY={2}>
      {/* Hero Section */}
      <Card padding={5} radius={2} tone="primary">
        <Stack gap={3}>
          <Heading size={2}>AI Content Generation System</Heading>
          <Text size={2}>
            Automatically generate high-quality, contextual descriptions for buyer guide wheel listings using Claude AI.
          </Text>
          <Flex gap={2} marginTop={2}>
            <Badge tone="positive">Claude Opus 4.6</Badge>
            <Badge tone="positive">Claude Sonnet 4.5</Badge>
            <Badge tone="caution">Per-Listing Generation</Badge>
            <Badge>Version Tracked</Badge>
          </Flex>
        </Stack>
      </Card>

      {/* Quick Start Guide */}
      <Section title="Quick Start Guide">
        <Stack gap={3}>
          <Text size={1} muted>
            Follow these steps to generate AI descriptions for buyer guides:
          </Text>

          <Grid gridTemplateColumns={[1, 1, 2]} gap={3}>
            <FeatureCard
              title="1. Configure Brand Voice"
              description="Set your company's tone and style guidelines in Global Settings. This ensures all generated content matches your brand."
              badge="Global Settings"
            />
            <FeatureCard
              title="2. Customize Prompt Templates"
              description="Edit the prompt templates in Buyer Guide Rules to define what information should be emphasized for each content type."
              badge="Buyer Guide Rules"
            />
            <FeatureCard
              title="3. Open a Buyer Guide"
              description="Navigate to any buyer guide document. Each guide represents a vehicle generation's wheel fitment options."
            />
            <FeatureCard
              title="4. Generate Descriptions"
              description="Click 'Generate AI Descriptions' button in the buyer guide. The system will generate content for all listings in the guide."
              badge="Action Button"
            />
          </Grid>
        </Stack>
      </Section>

      {/* Content Types */}
      <Section title="Content Types Generated">
        <Text size={1} muted style={{ marginBottom: '16px' }}>
          The AI generates four types of descriptions for each wheel listing:
        </Text>

        <Stack gap={3}>
          <Card padding={3} radius={2} style={{ borderLeft: '4px solid #0ea5e9' }}>
            <Stack gap={2}>
              <Flex align="center" gap={2}>
                <Text size={1} weight="bold">Overview</Text>
                <Badge tone="primary" fontSize={0}>Primary</Badge>
              </Flex>
              <Text size={1} muted>
                A comprehensive introduction to the wheel setup covering fitment type, wheel dimensions,
                and overall suitability for the vehicle.
              </Text>
            </Stack>
          </Card>

          <Card padding={3} radius={2} style={{ borderLeft: '4px solid #22c55e' }}>
            <Stack gap={2}>
              <Text size={1} weight="bold">Performance Analysis</Text>
              <Text size={1} muted>
                Detailed breakdown of how the wheel setup affects vehicle performance, including handling,
                acceleration impact, and ride quality.
              </Text>
            </Stack>
          </Card>

          <Card padding={3} radius={2} style={{ borderLeft: '4px solid #f59e0b' }}>
            <Stack gap={2}>
              <Text size={1} weight="bold">Ease of Fitment</Text>
              <Text size={1} muted>
                Clear explanation of installation requirements, modifications needed (spacers, camber,
                fender work), and overall difficulty level.
              </Text>
            </Stack>
          </Card>

          <Card padding={3} radius={2} style={{ borderLeft: '4px solid #a855f7' }}>
            <Stack gap={2}>
              <Text size={1} weight="bold">Tire Availability</Text>
              <Text size={1} muted>
                Analysis of tire options for the wheel size, including availability of performance,
                all-season, and specialty tires.
              </Text>
            </Stack>
          </Card>
        </Stack>
      </Section>

      {/* How It Works */}
      <Section title="How the System Works">
        <Stack gap={4}>
          <SubSection title="Step 1: Context Collection">
            <Text size={1} muted>
              The system collects comprehensive context for each listing including:
            </Text>
            <Box marginTop={2}>
              <Flex gap={2} wrap="wrap">
                <Badge>Vehicle Info</Badge>
                <Badge>Wheel Specs</Badge>
                <Badge>Tire Sizes</Badge>
                <Badge>Modifications</Badge>
                <Badge>Scores</Badge>
                <Badge>Tags</Badge>
                <Badge>Position</Badge>
              </Flex>
            </Box>
          </SubSection>

          <SubSection title="Step 2: Template Processing">
            <Text size={1} muted>
              Each content type has a prompt template. The system replaces {'{{variable}}'} placeholders
              with actual listing data to create a personalized prompt.
            </Text>
          </SubSection>

          <SubSection title="Step 3: AI Generation">
            <Text size={1} muted>
              The prompt is sent to Claude AI with your brand voice settings. Generated content
              is validated and returned.
            </Text>
          </SubSection>

          <SubSection title="Step 4: Storage">
            <Text size={1} muted>
              Content is saved with metadata (timestamp, rules version, context hash).
            </Text>
          </SubSection>
        </Stack>
      </Section>

      {/* Template Variables Reference */}
      <Section title="Template Variables Reference">
        <Text size={1} muted style={{ marginBottom: '16px' }}>
          Use these variables in your prompt templates:
        </Text>

        <Grid gridTemplateColumns={[1, 1, 2]} gap={4}>
          <SubSection title="Guide Context">
            <CodeBlock>{`{{guide.vehicle.make}}        // "BMW"
{{guide.vehicle.model}}       // "M3"
{{guide.vehicle.generation}}  // "G80"
{{guide.title}}               // Full title
{{guide.slug}}                // URL slug`}</CodeBlock>
          </SubSection>

          <SubSection title="Wheel Specifications">
            <CodeBlock>{`{{listing.wheelSize.setupType}}     // "Square"/"Staggered"
{{listing.wheelSize.front.diameter}} // "18"
{{listing.wheelSize.front.width}}    // "9.5"
{{listing.wheelSize.front.offset}}   // "+35"`}</CodeBlock>
          </SubSection>

          <SubSection title="Tire Information">
            <CodeBlock>{`{{listing.tireSize.front}}    // "265/35/18"
{{listing.tireSize.rear}}     // For staggered
{{listing.tireAvailability}}  // Rating`}</CodeBlock>
          </SubSection>

          <SubSection title="Modifications & Tags">
            <CodeBlock>{`{{listing.modifications.spacers}}
{{listing.modifications.camber}}
{{listing.modifications.fenders}}
{{listing.tags}}`}</CodeBlock>
          </SubSection>

          <SubSection title="Scoring Data">
            <CodeBlock>{`{{listing.scoring.totalScore}}
{{listing.scoring.easeOfFit}}
{{listing.scoring.performance}}
{{listing.scoring.tireAvailability}}`}</CodeBlock>
          </SubSection>

          <SubSection title="Relative Context">
            <CodeBlock>{`{{relative.fitmentType.name}}
{{relative.position.inFitmentType}}
{{relative.scoreComparison.vsAverage}}
{{relative.scoreComparison.vsBest}}`}</CodeBlock>
          </SubSection>
        </Grid>
      </Section>

      {/* Global Settings */}
      <Section title="Configuring Global Settings">
        <Stack gap={4}>
          <SubSection title="AI Provider Configuration">
            <Text size={1} muted>
              <strong>Provider:</strong> Opus 4.6 for highest quality and complex reasoning.
              Sonnet 4.5 (recommended) for best quality/cost balance.
              Haiku 4.5 for fast, economical generation.
            </Text>
            <Text size={1} muted style={{ marginTop: '8px' }}>
              <strong>Temperature:</strong> Controls creativity (0.2-0.4 = consistent, 0.6-0.8 = varied).
            </Text>
            <Text size={1} muted style={{ marginTop: '8px' }}>
              <strong>Max Tokens:</strong> 1024 is typically sufficient.
            </Text>
          </SubSection>

          <SubSection title="Brand Voice Settings">
            <Grid gridTemplateColumns={[1, 2]} gap={2}>
              <FeatureCard
                title="Tone Selection"
                description="Professional, Enthusiast-Friendly, Expert, or Casual"
              />
              <FeatureCard
                title="Voice Guidelines"
                description="Rules like 'Use active voice' or 'Be specific with measurements'"
              />
              <FeatureCard
                title="Terminology"
                description="Define preferred terms to use and terms to avoid"
              />
              <FeatureCard
                title="Disclaimer"
                description="Optional text appended to all generated content"
              />
            </Grid>
          </SubSection>
        </Stack>
      </Section>

      {/* API Reference */}
      <Section title="API Reference (For Developers)">
        <Stack gap={4}>
          <SubSection title="Context Preview API">
            <Text size={1} muted style={{ marginBottom: '8px' }}>
              View context data without generating:
            </Text>
            <CodeBlock>{`POST /api/ai/context-preview
{
  "buyerGuideId": "abc123"
  // OR "slug": "bmw-m3-g80"
}`}</CodeBlock>
          </SubSection>

          <SubSection title="Preview Generation API">
            <Text size={1} muted style={{ marginBottom: '8px' }}>
              Test generation without saving:
            </Text>
            <CodeBlock>{`POST /api/ai/preview-generation
{
  "buyerGuideId": "abc123",
  "fitmentTypeIndex": 0,
  "listingIndex": 0,
  "descriptionType": "overview"
}`}</CodeBlock>
          </SubSection>
        </Stack>
      </Section>

      {/* Best Practices */}
      <Section title="Best Practices">
        <Grid gridTemplateColumns={[1, 1, 2]} gap={3}>
          <Card padding={3} radius={2} tone="positive">
            <Stack gap={2}>
              <Text size={1} weight="bold">Do</Text>
              <Text size={1} muted>Use specific context variables</Text>
              <Text size={1} muted>Test with preview API first</Text>
              <Text size={1} muted>Update version when changing rules</Text>
              <Text size={1} muted>Keep max length 400-600 chars</Text>
              <Text size={1} muted>Review generated content</Text>
            </Stack>
          </Card>

          <Card padding={3} radius={2} tone="critical">
            <Stack gap={2}>
              <Text size={1} weight="bold">Avoid</Text>
              <Text size={1} muted>Auto-regeneration on large catalogs</Text>
              <Text size={1} muted>High temperature for technical content</Text>
              <Text size={1} muted>Generating without brand voice setup</Text>
              <Text size={1} muted>Ignoring the context hash</Text>
            </Stack>
          </Card>
        </Grid>
      </Section>

      {/* Version & Cost Info */}
      <Section title="Version Control & Costs">
        <Stack gap={3}>
          <SubSection title="Version Tracking">
            <Text size={1} muted>
              Increment version (e.g., 1.0.0 → 1.1.0) when modifying rules.
              Content stores the rules version for tracking.
            </Text>
          </SubSection>

          <SubSection title="Cost Considerations">
            <Text size={1} muted>
              Opus 4.6: ~$5/M input, ~$25/M output.
              Sonnet 4.5: ~$3/M input, ~$15/M output.
              Haiku 4.5: ~$0.80/M input, ~$4/M output.
              A typical guide with 20 listings generates ~80 descriptions.
            </Text>
          </SubSection>

          <Card padding={3} radius={2} tone="caution" marginTop={2}>
            <Flex align="center" gap={2}>
              <Text size={1} weight="semibold">Tip:</Text>
              <Text size={1}>
                Start with a few test guides before bulk regeneration.
              </Text>
            </Flex>
          </Card>
        </Stack>
      </Section>
    </Stack>
  )
}
